import { Link, notFound } from "@tanstack/react-router";
import { BlogPostView } from "./BlogPostView";
import { getBlogPost } from "@/content/blog";
import { pageMeta, ldScripts, breadcrumbLd, blogPostingLd } from "@/lib/seo";

// Factory that builds route options for a top-level blog post URL.
// Each blog post gets its own route file (src/routes/<slug>.tsx) that
// calls this so URLs are optimizeplus.net/<slug>/ instead of /blog/<slug>/.
export function blogRouteOptions(slug: string) {
  return {
    loader: () => {
      const post = getBlogPost(slug);
      if (!post) throw notFound();
      return { post };
    },
    head: ({
      loaderData,
    }: {
      loaderData?: { post: NonNullable<ReturnType<typeof getBlogPost>> };
    }) => {
      if (!loaderData) {
        return {
          meta: [
            { title: "Article not found · Optimize Plus" },
            { name: "robots", content: "noindex" },
          ],
        };
      }
      const { post } = loaderData;
      const title = `${post.title} · Optimize Plus`;
      const path = `/${slug}/`;
      const base = pageMeta({
        path,
        title,
        description: post.dek,
        type: "article",
        image: post.heroImage.src,
        keywords: [...post.tags, post.category, "Optimize Plus", "SEO Austin"],
      });
      const wordCount =
        post.intro.join(" ").split(/\s+/).length +
        post.sections.reduce((n, s) => n + s.p.join(" ").split(/\s+/).length, 0);
      return {
        ...base,
        meta: [
          ...base.meta,
          { property: "article:published_time", content: post.publishedISO },
          ...(post.updatedISO
            ? [{ property: "article:modified_time", content: post.updatedISO }]
            : []),
          { property: "article:section", content: post.category },
          ...post.tags.map((t) => ({ property: "article:tag", content: t })),
          { name: "author", content: "M. Umair Mansha" },
        ],
        scripts: ldScripts(
          blogPostingLd({
            headline: post.title,
            description: post.dek,
            path,
            datePublished: post.publishedISO,
            dateModified: post.updatedISO,
            image: post.heroImage.src,
            keywords: post.tags,
            articleSection: post.category,
            wordCount,
          }),
          // The HQ LocalBusiness node ships once sitewide from __root; emitting it
          // again here would duplicate the #localbusiness @id.
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog/" },
            { name: post.title, path },
          ]),
        ),
      };
    },
    notFoundComponent: () => (
      <div className="container-page py-32">
        <h1 className="font-display text-4xl">Article not found</h1>
        <Link to="/blog/" className="mt-4 inline-block text-primary">← Back to the publication</Link>
      </div>

    ),
    component: function BlogPostRoute() {
      const post = getBlogPost(slug);
      if (!post) throw notFound();
      return <BlogPostView post={post} />;
    },
  };
}
